import React, { useState, useMemo } from 'react';
import gisData from '../../data/gisAnalysis.json';

const fmt = (n) => n == null ? '-' : Number(n).toLocaleString('tr-TR');
const pct = (a, b) => b ? Math.round(a * 100 / b) : 0;

const C = {
  blue: '#2563eb', green: '#16a34a', purple: '#7c3aed', orange: '#f59e0b', red: '#dc2626', gray: '#64748b',
};

const GISDataAnalysis = () => {
  const [excelTab, setExcelTab] = useState('matched');
  const [shpTab, setShpTab] = useState('matched');
  const [excelPage, setExcelPage] = useState(0);
  const [shpPage, setShpPage] = useState(0);
  const [filterIlce, setFilterIlce] = useState('');
  const [filterScore, setFilterScore] = useState('all');
  const [searchText, setSearchText] = useState('');
  const PAGE = 50;

  const src = gisData.sources;
  const exM = gisData.matchExcel.matched;
  const exU = gisData.matchExcel.unmatched;
  const spM = gisData.matchShp.matched;
  const spU = gisData.matchShp.unmatched;

  const exHigh = useMemo(() => exM.filter(m => m.score >= 0.8), [exM]);
  const spHigh = useMemo(() => spM.filter(m => m.score >= 0.8), [spM]);

  const ilceList = useMemo(() => {
    const s = new Set();
    Object.keys(gisData.askiIlce).forEach(i => s.add(i));
    return [...s].sort();
  }, []);

  const filterList = (list, showScore) => {
    let filtered = list;
    if (filterIlce) filtered = filtered.filter(r => r.ilce === filterIlce);
    if (showScore && filterScore === 'high') filtered = filtered.filter(r => r.score >= 0.8);
    if (showScore && filterScore === 'low') filtered = filtered.filter(r => r.score < 0.6);
    if (searchText) {
      const q = searchText.toLowerCase();
      filtered = filtered.filter(r =>
        (r.askiName || '').toLowerCase().includes(q) ||
        (r.cleanName || '').toLowerCase().includes(q) ||
        (r.gisName || '').toLowerCase().includes(q) ||
        (r.shpName || '').toLowerCase().includes(q) ||
        (r.aboneno || '').includes(q)
      );
    }
    return filtered;
  };

  const badge = (score) => {
    const color = score >= 0.8 ? C.green : score >= 0.6 ? C.orange : C.red;
    return { display: 'inline-block', padding: '1px 7px', borderRadius: 5, fontSize: 11, fontWeight: 700, background: color + '18', color };
  };

  const tabBtn = (active) => ({
    padding: '6px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12,
    background: active ? '#1e293b' : '#f1f5f9', color: active ? '#fff' : '#64748b',
  });

  const filterBar = { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12, padding: '10px 14px', background: '#f8fafc', borderRadius: 10 };
  const sel = { padding: '5px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, background: '#fff' };
  const inp = { padding: '5px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, background: '#fff', flex: 1, minWidth: 150 };
  const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 12 };
  const th = { textAlign: 'left', padding: '7px 10px', background: '#f1f5f9', borderBottom: '2px solid #e2e8f0', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em', color: '#475569', position: 'sticky', top: 0 };
  const td = { padding: '6px 10px', borderBottom: '1px solid #f1f5f9', fontSize: 12 };
  const srcTag = (color) => ({ display: 'inline-block', padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: color + '15', color, marginRight: 4 });

  const renderPagination = (total, page, setPage) => {
    const pages = Math.ceil(total / PAGE);
    if (pages <= 1) return null;
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 10, fontSize: 12 }}>
        <button disabled={page === 0} onClick={() => setPage(p => p - 1)} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 12 }}>Onceki</button>
        <span style={{ color: '#64748b' }}>Sayfa {page + 1} / {pages} ({fmt(total)} kayit)</span>
        <button disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)} style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 12 }}>Sonraki</button>
      </div>
    );
  };

  return (
    <div style={{ padding: '20px 24px', fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif', color: '#1e293b' }}>

      {/* HEADER */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 22, fontWeight: 800, background: 'linear-gradient(135deg,#1e40af,#7c3aed)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          GIS Veri Karsilastirmasi
        </div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Farkli veri kaynaklarinin karsilastirilmasi ve eslesme sonuclari</div>
      </div>

      {/* VERI KAYNAKLARI */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Veri Kaynaklari</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>

          <div style={{ background: '#fff', borderRadius: 12, padding: '16px 18px', border: '1px solid #bfdbfe', borderLeft: '4px solid ' + C.blue }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: C.blue, marginBottom: 6 }}>Kaynak 1: ASKI SOAP</div>
            <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>{src.aski.desc}</div>
            <div style={{ display: 'flex', gap: 16 }}>
              <div><span style={{ fontSize: 22, fontWeight: 800, color: C.blue }}>{fmt(src.aski.uniqueAbone)}</span><div style={{ fontSize: 10, color: '#94a3b8' }}>benzersiz abone</div></div>
              <div><span style={{ fontSize: 22, fontWeight: 800, color: '#94a3b8' }}>{fmt(src.aski.totalRecords)}</span><div style={{ fontSize: 10, color: '#94a3b8' }}>toplam kayit</div></div>
            </div>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 6 }}>S3: {src.aski.s3Key}</div>
          </div>

          <div style={{ background: '#fff', borderRadius: 12, padding: '16px 18px', border: '1px solid #bbf7d0', borderLeft: '4px solid ' + C.green }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: C.green, marginBottom: 6 }}>Kaynak 2: Shapefile</div>
            <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>{src.shapefile.desc}</div>
            <div style={{ display: 'flex', gap: 16 }}>
              <div><span style={{ fontSize: 22, fontWeight: 800, color: C.green }}>{src.shapefile.total}</span><div style={{ fontSize: 10, color: '#94a3b8' }}>park</div></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ ...srcTag(C.green) }}>KOORDINAT VAR</span>
                <span style={{ ...srcTag(C.orange) }}>ALAN YOK</span>
              </div>
            </div>
          </div>

          <div style={{ background: '#fff', borderRadius: 12, padding: '16px 18px', border: '1px solid #ddd6fe', borderLeft: '4px solid ' + C.purple }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: C.purple, marginBottom: 6 }}>Kaynak 3: GIS Excel</div>
            <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>{src.gisExcel.desc}</div>
            <div style={{ display: 'flex', gap: 16 }}>
              <div><span style={{ fontSize: 22, fontWeight: 800, color: C.purple }}>{fmt(src.gisExcel.total)}</span><div style={{ fontSize: 10, color: '#94a3b8' }}>park</div></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ ...srcTag(C.red) }}>KOORDINAT YOK</span>
                <span style={{ ...srcTag(C.green) }}>ALAN VAR</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* OZET BAR */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Excel Eslesen', val: exM.length, sub: `/ ${src.aski.uniqueAbone}`, pctVal: pct(exM.length, src.aski.uniqueAbone), color: C.purple },
          { label: 'Excel Guvenilir', val: exHigh.length, sub: '>=80%', pctVal: pct(exHigh.length, src.aski.uniqueAbone), color: C.green },
          { label: 'Shapefile Eslesen', val: spM.length, sub: `/ ${src.aski.uniqueAbone}`, pctVal: pct(spM.length, src.aski.uniqueAbone), color: C.green },
          { label: 'Shapefile Guvenilir', val: spHigh.length, sub: 'koordinat atanabilir', pctVal: pct(spHigh.length, src.aski.uniqueAbone), color: '#059669' },
        ].map((item, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 10, padding: '14px 16px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '.04em' }}>{item.label}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: item.color }}>{fmt(item.val)}</div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>{item.sub} ({item.pctVal}%)</div>
          </div>
        ))}
      </div>

      {/* FILTRELER */}
      <div style={filterBar}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>Filtre:</span>
        <select value={filterIlce} onChange={e => { setFilterIlce(e.target.value); setExcelPage(0); setShpPage(0); }} style={sel}>
          <option value="">Tum ilceler</option>
          {ilceList.map(i => <option key={i} value={i}>{i} ({gisData.askiIlce[i] || 0})</option>)}
        </select>
        <select value={filterScore} onChange={e => { setFilterScore(e.target.value); setExcelPage(0); setShpPage(0); }} style={sel}>
          <option value="all">Tum guven</option>
          <option value="high">Yuksek guven (&gt;=80%)</option>
          <option value="low">Dusuk guven (&lt;60%)</option>
        </select>
        <input placeholder="Ara (park adi, aboneno)..." value={searchText} onChange={e => { setSearchText(e.target.value); setExcelPage(0); setShpPage(0); }} style={inp} />
      </div>

      {/* EXCEL KARSILASTIRMA */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <span style={srcTag(C.blue)}>ASKI</span>
          <span style={{ fontSize: 14 }}>&#8596;</span>
          <span style={srcTag(C.purple)}>GIS Excel</span>
          <span style={{ fontSize: 15, fontWeight: 700, marginLeft: 6 }}>Karsilastirma</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button style={tabBtn(excelTab === 'matched')} onClick={() => { setExcelTab('matched'); setExcelPage(0); }}>Eslesen ({fmt(filterList(exM, true).length)})</button>
            <button style={tabBtn(excelTab === 'unmatched')} onClick={() => { setExcelTab('unmatched'); setExcelPage(0); }}>Eslesmyen ({fmt(filterList(exU, false).length)})</button>
          </div>
        </div>
        <div style={{ maxHeight: 420, overflow: 'auto', borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <table style={tbl}>
            <thead>
              <tr>
                <th style={th}>Aboneno</th>
                <th style={th}><span style={srcTag(C.blue)}>ASKI</span> Abone Adi</th>
                <th style={th}>Ilce</th>
                {excelTab === 'matched' && <>
                  <th style={th}><span style={srcTag(C.purple)}>GIS</span> Park Adi</th>
                  <th style={th}>Alan (m2)</th>
                  <th style={th}>Guven</th>
                </>}
              </tr>
            </thead>
            <tbody>
              {(excelTab === 'matched' ? filterList(exM, true) : filterList(exU, false))
                .slice(excelPage * PAGE, (excelPage + 1) * PAGE)
                .map((r, i) => (
                  <tr key={i} style={{ background: i % 2 ? '#fafbfc' : '#fff' }}>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 11, color: '#64748b' }}>{r.aboneno}</td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.askiName}</div>
                      {r.cleanName !== r.askiName && <div style={{ fontSize: 10, color: '#94a3b8' }}>{r.cleanName}</div>}
                    </td>
                    <td style={{ ...td, fontSize: 11 }}>{r.ilce}</td>
                    {excelTab === 'matched' && <>
                      <td style={{ ...td, fontWeight: 600, color: C.purple }}>{r.gisName}</td>
                      <td style={{ ...td, fontSize: 11, fontWeight: 600 }}>{r.alan ? fmt(Math.round(r.alan)) : '-'}</td>
                      <td style={td}><span style={badge(r.score)}>{Math.round(r.score * 100)}%</span></td>
                    </>}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {renderPagination(
          (excelTab === 'matched' ? filterList(exM, true) : filterList(exU, false)).length,
          excelPage, setExcelPage
        )}
      </div>

      {/* SHAPEFILE KARSILASTIRMA */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <span style={srcTag(C.blue)}>ASKI</span>
          <span style={{ fontSize: 14 }}>&#8596;</span>
          <span style={srcTag(C.green)}>Shapefile</span>
          <span style={{ fontSize: 15, fontWeight: 700, marginLeft: 6 }}>Karsilastirma (Koordinat)</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button style={tabBtn(shpTab === 'matched')} onClick={() => { setShpTab('matched'); setShpPage(0); }}>Eslesen ({fmt(filterList(spM, true).length)})</button>
            <button style={tabBtn(shpTab === 'unmatched')} onClick={() => { setShpTab('unmatched'); setShpPage(0); }}>Eslesmyen ({fmt(filterList(spU, false).length)})</button>
          </div>
        </div>
        <div style={{ maxHeight: 420, overflow: 'auto', borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <table style={tbl}>
            <thead>
              <tr>
                <th style={th}>Aboneno</th>
                <th style={th}><span style={srcTag(C.blue)}>ASKI</span> Abone Adi</th>
                <th style={th}>Ilce</th>
                {shpTab === 'matched' && <>
                  <th style={th}><span style={srcTag(C.green)}>SHP</span> Park Adi</th>
                  <th style={th}>SHP Ilce</th>
                  <th style={th}>Guven</th>
                </>}
              </tr>
            </thead>
            <tbody>
              {(shpTab === 'matched' ? filterList(spM, true) : filterList(spU, false))
                .slice(shpPage * PAGE, (shpPage + 1) * PAGE)
                .map((r, i) => (
                  <tr key={i} style={{ background: i % 2 ? '#fafbfc' : '#fff' }}>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 11, color: '#64748b' }}>{r.aboneno}</td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.askiName}</div>
                      {r.cleanName !== r.askiName && <div style={{ fontSize: 10, color: '#94a3b8' }}>{r.cleanName}</div>}
                    </td>
                    <td style={{ ...td, fontSize: 11 }}>{r.ilce}</td>
                    {shpTab === 'matched' && <>
                      <td style={{ ...td, fontWeight: 600, color: C.green }}>{r.shpName}</td>
                      <td style={{ ...td, fontSize: 11 }}>{r.shpIlce}</td>
                      <td style={td}><span style={badge(r.score)}>{Math.round(r.score * 100)}%</span></td>
                    </>}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {renderPagination(
          (shpTab === 'matched' ? filterList(spM, true) : filterList(spU, false)).length,
          shpPage, setShpPage
        )}
      </div>

      {/* ILCE BAZLI */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>Ilce Bazli Dagılım</div>
        <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <table style={tbl}>
            <thead>
              <tr>
                <th style={th}>Ilce</th>
                <th style={th}><span style={srcTag(C.blue)}>ASKI</span></th>
                <th style={th}><span style={srcTag(C.purple)}>GIS Excel</span></th>
                <th style={th}><span style={srcTag(C.green)}>Shapefile</span></th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(gisData.askiIlce).sort((a, b) => b[1] - a[1]).map(([ilce, cnt], i) => (
                <tr key={ilce} style={{ background: i % 2 ? '#fafbfc' : '#fff', cursor: 'pointer' }} onClick={() => setFilterIlce(ilce === filterIlce ? '' : ilce)}>
                  <td style={{ ...td, fontWeight: 700, color: ilce === filterIlce ? C.blue : '#1e293b' }}>{ilce}</td>
                  <td style={td}>{fmt(cnt)}</td>
                  <td style={td}>{fmt(gisData.xlsIlce[ilce] || 0)}</td>
                  <td style={td}>{fmt(gisData.shpIlce[ilce] || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default GISDataAnalysis;
